/**
 * Shadow-piercing focus enumeration shared by any component that manages keyboard
 * focus (a focus trap, a roving tabindex, a dialog).
 *
 * A light-DOM CSS query (`button, [tabindex]`, …) cannot see the real focusable
 * control of a custom element like `<caos-button>`, whose `<button>` lives in its
 * own shadow root and whose host carries no `tabindex`/`delegatesFocus`. Enumerating
 * with `querySelectorAll` therefore returns `[]` for such actions, which silently
 * breaks a trap. These helpers walk the FLATTENED tree — descending into every open
 * shadow root and resolving `<slot>` assignments — so the actual interactive leaves
 * are found in tab order.
 */

const FOCUSABLE_SELECTOR =
  'a[href],area[href],button:not([disabled]),input:not([disabled]),' +
  'select:not([disabled]),textarea:not([disabled]),' +
  'audio[controls],video[controls],[contenteditable]:not([contenteditable="false"]),' +
  '[tabindex]:not([tabindex="-1"])';

/**
 * Does `el` hide its whole subtree from focus? Evaluated per node during the
 * top-down flattened walk so the answer propagates across shadow/slot boundaries
 * that `Element.closest()` cannot cross (it stops at the element's own root).
 * `hidden`/`inert`/`aria-hidden` on a slotted custom-element HOST must suppress the
 * inner `<button>` in its shadow root; walking downward with an inherited flag is
 * the only correct way to see that relationship.
 */
function isHiddenBarrier(el: Element): boolean {
  const h = el as HTMLElement & { inert?: boolean };
  if (h.hidden === true) return true;
  if (h.inert === true) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;

  const view = el.ownerDocument?.defaultView;
  const style = view?.getComputedStyle(el as HTMLElement);
  if (style) {
    if (style.display === 'none') return true;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return true;
    // A zero-size box that CLIPS its overflow hides its descendants from view even
    // though those descendants keep their own client rects — so a leaf-only rect
    // check misses it. `overflow: visible` (incl. display:contents wrappers) does
    // NOT clip and is not a barrier.
    const clips =
      style.overflow !== 'visible' ||
      style.overflowX !== 'visible' ||
      style.overflowY !== 'visible';
    if (clips && (el as HTMLElement).offsetWidth === 0 && (el as HTMLElement).offsetHeight === 0) {
      return true;
    }
  }
  return false;
}

/** A matching, rendered, enabled leaf — assuming no ancestor barrier (checked upstream). */
function isFocusableLeaf(el: Element): el is HTMLElement {
  if (!el.matches(FOCUSABLE_SELECTOR)) return false;
  // display:none anywhere in the flattened ancestry, or detached, yields no rects.
  return (el as HTMLElement).getClientRects().length > 0;
}

/**
 * Every focusable control under `root`, in flattened-tree (tab) order, descending
 * into any OPEN shadow root and resolving `<slot>` assignments. Skips disabled,
 * hidden, `aria-hidden`, `inert`, `visibility:hidden`, and zero-size-clipped
 * elements — including when the hiding attribute sits on a slotted host across a
 * shadow boundary (the inherited `hidden` flag carries it into the shadow root).
 */
export function collectFocusables(root: ParentNode): HTMLElement[] {
  const out: HTMLElement[] = [];
  const seen = new Set<Element>();

  const visit = (el: Element, hidden: boolean): void => {
    if (seen.has(el)) return;
    seen.add(el);

    const barred = hidden || isHiddenBarrier(el);
    if (!barred && isFocusableLeaf(el)) out.push(el);

    // Follow the flattened tree so slotted / shadowed controls are reached in order,
    // carrying the barrier flag downward across every boundary.
    if (el instanceof HTMLSlotElement) {
      const assigned = el.assignedElements({ flatten: true });
      (assigned.length ? assigned : Array.from(el.children)).forEach((c) => visit(c, barred));
      return;
    }
    const shadow = el.shadowRoot;
    if (shadow && shadow.mode === 'open') {
      Array.from(shadow.children).forEach((c) => visit(c, barred));
      return;
    }
    Array.from(el.children).forEach((c) => visit(c, barred));
  };

  Array.from(root.children).forEach((c) => visit(c, false));
  return out;
}

/**
 * The deepest focused element, piercing open shadow roots. `document.activeElement`
 * only reports the outermost host of a shadow-encapsulated control (e.g. it returns
 * `<caos-button>`, never the inner `<button>`); this descends to the real leaf so it
 * can be compared against the entries `collectFocusables` returns.
 */
export function deepActiveElement(root: DocumentOrShadowRoot = document): Element | null {
  let active = root.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/**
 * CAOS-651 — keyboard focus for a panel that opens onto the page's top layer.
 *
 * The app launcher, the navigation gadget, the object caret and the overflow menu each lift their
 * panel out to `document.body` so nothing can clip it. That also lifts it out of the tab order: the
 * panel is in front of the reader and the next Tab lands somewhere on the page behind it. This is
 * the one behaviour all four share, so it cannot drift into four variations:
 *  - `open()` records the control that opened the panel and moves focus into it;
 *  - `trap()` keeps Tab and Shift+Tab inside the panel, stepping through its controls in order and
 *    wrapping at either end;
 *  - `close(true)` gives focus back to that control, if it is still on the page. `close(false)` —
 *    a click somewhere else — leaves focus where the click put it.
 *
 * It is the trap `<caos-overlay>` keeps, on the same shadow-piercing lookup. A panel whose controls
 * are deliberately out of the tab order (a menu that moves between its items with the arrow keys)
 * passes those items as `items`, so Tab cycles through them too.
 */
export interface PanelFocus {
  /**
   * Record the opener and move focus into the panel: to `initial`, else the first control, else the
   * panel itself. Without an explicit `opener`, whatever held focus outside the panel is the opener;
   * opened again while focus is already inside it (or nowhere in particular), it keeps the one it had.
   */
  open(options?: { opener?: HTMLElement | null; initial?: HTMLElement | null }): void;
  /** Keep a Tab keypress inside the panel. Returns whether it handled the key. */
  trap(e: KeyboardEvent): boolean;
  /** Forget the opener; when `restore`, return focus to it if it is still connected. */
  close(restore: boolean): void;
}

export function panelFocus(
  panel: HTMLElement,
  items: () => HTMLElement[] = () => collectFocusables(panel),
): PanelFocus {
  let opener: HTMLElement | null = null;

  const focusPanel = (): void => {
    if (!panel.hasAttribute('tabindex')) panel.tabIndex = -1;
    panel.focus();
  };

  return {
    open(options = {}) {
      const active = deepActiveElement();
      // The page itself is not a control to hand focus back to: a click that focused nothing leaves
      // `<body>` active, and returning focus there would only drop it at the top of the document.
      const outside =
        active instanceof HTMLElement && active !== active.ownerDocument.body && !panel.contains(active)
          ? active
          : null;
      opener = options.opener ?? outside ?? opener;
      const target = options.initial ?? items()[0];
      if (target) target.focus();
      else focusPanel();
    },

    trap(e) {
      if (e.key !== 'Tab') return false;
      e.preventDefault();
      const controls = items();
      if (controls.length === 0) {
        focusPanel();
        return true;
      }
      const at = controls.indexOf(deepActiveElement() as HTMLElement);
      const last = controls.length - 1;
      const next = e.shiftKey
        ? controls[at <= 0 ? last : at - 1]
        : controls[at < 0 || at >= last ? 0 : at + 1];
      next?.focus();
      return true;
    },

    close(restore) {
      const to = opener;
      opener = null;
      if (restore && to && to.isConnected) to.focus();
    },
  };
}
